import React, { useState, useEffect, useCallback } from 'react';
import LoadingState from '../common/LoadingState';
import { 
  Assembly, 
  AssemblyStatus, 
  AssemblyType,
  AssemblyFilterDto 
} from '../../types/assembly';
import { 
  CalendarDaysIcon,
  ClockIcon,
  UserGroupIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  VideoCameraIcon,
  BuildingOffice2Icon,
  FunnelIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { assemblyService } from '../../services/assemblyService';
import { useAssembly } from '../../context/AssemblyContext';
import { getAssemblyTypeLabel, getAssemblyStatusLabel, getAssemblyModalityLabel, getAssemblyStatusBadgeClass, getAssemblyStatusIconClass } from '../../utils/assemblyLabels';
import { formatAssemblyCalendarDateEs, formatAssemblyTimeEs } from '../../utils/assemblyDate';

// Nuevo componente para ingreso de código
import AssemblyCodeForm from './AssemblyCodeForm';

interface AssemblySelectorProps {
  onAssemblySelect: (assembly: Assembly) => void;
  selectedAssemblyId?: string;
  showFilters?: boolean;
}

const AssemblySelector: React.FC<AssemblySelectorProps> = ({
  onAssemblySelect,
  selectedAssemblyId,
  showFilters = true
}) => {
  const [assemblies, setAssemblies] = useState<Assembly[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AssemblyFilterDto>({});
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;
  const { user } = useAuth();
  
  // Intentar usar el contexto de Assembly, pero manejarlo si no está disponible
  let joinAssembly: ((assemblyId: string) => Promise<void>) | undefined;
  try {
    const assemblyContext = useAssembly();
    joinAssembly = assemblyContext.joinAssembly;
  } catch (error) {
    // El contexto no está disponible, continuar sin él
    console.warn('AssemblyContext not available, assembly joining will be handled externally');
  }
  
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeLoading, setCodeLoading] = useState(false);

  // Función para cargar las asambleas
  const loadAssemblies = useCallback(async (filterOptions?: AssemblyFilterDto, pageOverride?: number) => {
    try {
      setLoading(true);
      setError(null);
      const requestPage = pageOverride ?? page;
      const requestFilters = {
        ...filters,
        ...filterOptions,
        page: requestPage,
        limit
      };
      const data = await assemblyService.getAssemblies(requestFilters);
      setAssemblies(data.assemblies);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || data.assemblies.length);
      setPage(data.page || requestPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando asambleas');
      console.error('Error loading assemblies:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, limit]);

  // Cargar asambleas al montar el componente
  useEffect(() => {
    loadAssemblies();
  }, [loadAssemblies]);

  // Aplicar filtros
  const applyFilters = () => {
    setPage(1);
    loadAssemblies(filters, 1);
    setShowFilterPanel(false);
  };

  // Limpiar filtros
  const clearFilters = () => {
    setFilters({});
    setPage(1);
    loadAssemblies({}, 1);
    setShowFilterPanel(false);
  };

  // Obtener el ícono de estado
  const getStatusIcon = (status: AssemblyStatus) => {
    const iconClass = `h-5 w-5 ${getAssemblyStatusIconClass(status)}`;
    switch (status) {
      case AssemblyStatus.SCHEDULED:
        return <ClockIcon className={iconClass} />;
      case AssemblyStatus.IN_PROGRESS:
        return <PlayIcon className={iconClass} />;
      case AssemblyStatus.COMPLETED:
        return <CheckCircleIcon className={iconClass} />;
      case AssemblyStatus.CANCELLED:
        return <XCircleIcon className={iconClass} />;
      default:
        return <ClockIcon className={iconClass} />;
    }
  };

  const formatDate = (dateString: string) => {
    return formatAssemblyCalendarDateEs(dateString, dateString);
  };

  const formatTime = (timeString: string) => {
    return formatAssemblyTimeEs(timeString, 'Por confirmar');
  };

    // Handler para propietarios
  const handleCodeSubmit = async (code: string) => {
    setCodeLoading(true);
    setCodeError(null);
    try {
      const assembly = await assemblyService.getAssemblyByCode(code);
      setAssemblies([assembly]);
      
      // Guardar en localStorage para persistencia
      localStorage.setItem('selectedAssembly', JSON.stringify(assembly));
      
      onAssemblySelect(assembly);
      
      // Usar el contexto para unirse a la asamblea solo si está disponible
      if (joinAssembly) {
        await joinAssembly(assembly.id);
        console.log('✅ Usuario conectado a la asamblea');
      }
    } catch (err) {
      setCodeError(err instanceof Error ? err.message : 'Código inválido o asamblea no encontrada');
    } finally {
      setCodeLoading(false);
    }
  };

  // Mostrar formulario solo para propietarios
  if (user?.role === 'owner') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px]">
        <AssemblyCodeForm onCodeSubmit={handleCodeSubmit} />
        {codeLoading && <div className="mt-4 text-blue-600">Buscando asamblea...</div>}
        {codeError && <div className="mt-2 text-red-500">{codeError}</div>}
      </div>
    );
  }

  if (loading) {
    return <LoadingState message="Cargando asambleas..." />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-700">Error: {error}</span>
        </div>
        <button
          onClick={() => loadAssemblies()}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header con filtros */}
      {showFilters && (
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Seleccionar Asamblea
          </h2>
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FunnelIcon className="h-4 w-4 mr-1" />
            Filtros
          </button>
        </div>
      )}

      {/* Panel de filtros */}
      {showFilterPanel && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as AssemblyStatus || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los estados</option>
                <option value={AssemblyStatus.SCHEDULED}>Programadas</option>
                <option value={AssemblyStatus.IN_PROGRESS}>En Progreso</option>
                <option value={AssemblyStatus.COMPLETED}>Completadas</option>
                <option value={AssemblyStatus.CANCELLED}>Canceladas</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo
              </label>
              <select
                value={filters.type || ''}
                onChange={(e) => setFilters({ ...filters, type: e.target.value as AssemblyType || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los tipos</option>
                <option value={AssemblyType.ORDINARY}>Ordinaria</option>
                <option value={AssemblyType.EXTRAORDINARY}>Extraordinaria</option>
                <option value={AssemblyType.INFORMATIVE}>Informativa</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Modalidad
              </label>
              <select
                value={filters.hasVirtualLink !== undefined ? (filters.hasVirtualLink ? 'virtual' : 'presencial') : ''}
                onChange={(e) => {
                  if (e.target.value === '') {
                    const { hasVirtualLink, ...rest } = filters;
                    setFilters(rest);
                  } else {
                    setFilters({ ...filters, hasVirtualLink: e.target.value === 'virtual' });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas las modalidades</option>
                <option value="virtual">Virtual/Híbrida</option>
                <option value="presencial">Presencial</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Limpiar
            </button>
            <button
              onClick={applyFilters}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Aplicar Filtros
            </button>
          </div>
        </div>
      )}

      {/* Lista de asambleas */}
      {assemblies.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <UserGroupIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p>No se encontraron asambleas</p>
          <p className="text-sm">Modifica los filtros o crea una nueva asamblea</p>
        </div>
      ) : (
        <div className="space-y-3">
          {assemblies.map((assembly) => (
            <div
              key={assembly.id}
              onClick={() => onAssemblySelect(assembly)}
              className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                selectedAssemblyId === assembly.id 
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-medium text-gray-900 truncate">
                      {assembly.title}
                    </h3>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getAssemblyStatusBadgeClass(assembly.status)}`}>
                      {getStatusIcon(assembly.status)}
                      <span className="ml-1">{getAssemblyStatusLabel(assembly.status)}</span>
                    </span>
                  </div>

                  {assembly.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {assembly.description}
                    </p>
                  )}

                  <div className="flex flex-wrap items-center text-sm text-gray-500 gap-x-4 gap-y-2">
                    <div className="flex items-center">
                      <CalendarDaysIcon className="h-4 w-4 mr-1" />
                      {formatDate(assembly.scheduledDate)} - {formatTime(assembly.startTime)}
                    </div>

                    <div className="flex items-center">
                      {assembly.isVirtual ? (
                        <VideoCameraIcon className="h-4 w-4 mr-1" />
                      ) : (
                        <BuildingOffice2Icon className="h-4 w-4 mr-1" />
                      )}
                      {getAssemblyModalityLabel(assembly.isVirtual)}
                    </div>

                    {assembly.isVirtual ? (
                      <div className="flex items-center">
                        <VideoCameraIcon className="h-4 w-4 mr-1" />
                        {assembly.meetingLink ? (
                          <a
                            href={assembly.meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            Conectarse
                          </a>
                        ) : (
                          <span className="text-amber-700">Enlace pendiente</span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <MapPinIcon className="h-4 w-4 mr-1" />
                        <span>{assembly.location || 'Lugar por definir'}</span>
                      </div>
                    )}

                    <div className="text-blue-600 font-medium">
                      {getAssemblyTypeLabel(assembly.type)}
                    </div>
                  </div>
                </div>

                {selectedAssemblyId === assembly.id && (
                  <CheckCircleIcon className="h-6 w-6 text-blue-600 flex-shrink-0 ml-2" />
                )}
              </div>
            </div>
          ))}

          {totalPages > 1 && (
            <div className="pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-sm text-gray-600">
                Mostrando {total === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, total)} de {total}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    const nextPage = Math.max(1, page - 1);
                    setPage(nextPage);
                    loadAssemblies(undefined, nextPage);
                  }}
                  disabled={page <= 1}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <span className="text-sm text-gray-600">
                  Página {page} de {totalPages}
                </span>
                <button
                  onClick={() => {
                    const nextPage = Math.min(totalPages, page + 1);
                    setPage(nextPage);
                    loadAssemblies(undefined, nextPage);
                  }}
                  disabled={page >= totalPages}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssemblySelector;